import { useCallback } from "react";
import useAPI from "./useApi";
import { stripServerLayoutSettings } from "../utils/layoutLocalStorage";
import { commitAppSettingsPatch, enqueueAppSettingsSave, getAppSettings, isAppSettingsSeeded } from "../utils/appSettingsStore";

const getBackendUrl = () => process.env.REACT_APP_BACKEND_URL || '';

export const useRegisterUser = () => {
    const { data, error, loading, request } = useAPI();

    const registerUser = (email, password, referralCode = null, sourceParams = null) => {
        const url = `${getBackendUrl()}/auth/register`
        const body = { 
            email, 
            password
        };
        
        if (referralCode) {
            body.referral_code = referralCode;
        }
        
        if (sourceParams && Object.keys(sourceParams).length > 0) {
            body.registration_source = sourceParams;
            console.log('[useBackend] Adding registration_source to request:', sourceParams);
        } else {
            console.log('[useBackend] No sourceParams provided or empty:', sourceParams);
        }
        
        console.log('[useBackend] Registration request body (without password):', {
            ...body,
            password: '[REDACTED]'
        });
        
        return request(url, {},'POST', body);
    }

    return { data, error, loading, registerUser };
}

export const useLoginUser = () => {
    const { data, error, loading, request } = useAPI();

    const loginUser = (email, password, sourceParams = null) => {
        const url = `${getBackendUrl()}/auth/login`
        const body = { email, password };
        
        if (sourceParams && Object.keys(sourceParams).length > 0) {
            body.login_source = sourceParams;
        }
        
        return request(url, {},'POST', body);
    }

    return { data, error, loading, loginUser };
}

export const useGoogleLogin = () => {
    const { data, error, loading, request } = useAPI();

    const googleLogin = (idToken, sourceParams = null) => {
        const url = `${getBackendUrl()}/auth/google/login`
        const body = { id_token: idToken };
        
        console.log('[Frontend] Google Login - Making request:', {
            url: url,
            backendUrl: getBackendUrl(),
            hasIdToken: !!idToken,
            idTokenLength: idToken ? idToken.length : 0,
            hasSourceParams: !!sourceParams
        });
        
        if (sourceParams && Object.keys(sourceParams).length > 0) {
            body.login_source = sourceParams;
        }
        
        return request(url, {},'POST', body);
    }

    return { data, error, loading, googleLogin };
}

export const useGoogleRegister = () => {
    const { data, error, loading, request } = useAPI();

    const googleRegister = (idToken, referralCode = null, sourceParams = null) => {
        const url = `${getBackendUrl()}/auth/google/register`
        const body = { id_token: idToken };
        
        console.log('[Frontend] Google Register - Making request:', {
            url: url,
            backendUrl: getBackendUrl(),
            hasIdToken: !!idToken,
            idTokenLength: idToken ? idToken.length : 0,
            hasReferralCode: !!referralCode,
            hasSourceParams: !!sourceParams
        });
        
        if (referralCode) {
            body.referral_code = referralCode;
        }
        
        if (sourceParams && Object.keys(sourceParams).length > 0) {
            body.registration_source = sourceParams;
        }
        
        return request(url, {},'POST', body);
    }

    return { data, error, loading, googleRegister };
}

export const useRequestPasswordReset = () => {
    const { data, error, loading, request } = useAPI();

    const requestPasswordReset = (email) => {
        const url = `${getBackendUrl()}/auth/request_password_reset`;
        return request(url, {}, 'POST', { email });
    };

    return { data, error, loading, requestPasswordReset };
};

export const useResetPassword = () => {
    const { data, error, loading, request } = useAPI();

    const resetPassword = (token, newPassword) => {
        const url = `${getBackendUrl()}/auth/reset_password`;
        return request(url, {}, 'POST', { token, new_password: newPassword });
    };

    return { data, error, loading, resetPassword };
};

export const useRefreshAccessToken = () => {
    const { data, error, loading, request } = useAPI();

    const refreshAccessToken = (jwt) => {
        const url = `${getBackendUrl()}/tradestation/refresh_token`
        return request(url, {},'PUT');
    };

    return { data, error, loading, refreshAccessToken };
}

export const useGetStoredCredentials = () => {
    const { data, error, loading, request } = useAPI();

    const getStoredCredentials = (jwt) => {
        const url = `${getBackendUrl()}/tradestation/credentials`
        return request(url, {},'GET');
    };

    return { data, error, loading, getStoredCredentials };
}

export const useTradeStationAuth = (jwt) => {
    const { request, loading } = useAPI();
    const clientId = process.env.REACT_APP_TRADESTATION_CLIENT_ID;
    // Use the same redirect URI that the backend expects
    const redirectUri = process.env.REACT_APP_TRADESTATION_REDIRECT_URI || 'http://localhost:3001';
    const audience = process.env.REACT_APP_TRADESTATION_AUDIENCE;
    const scope = process.env.REACT_APP_TRADESTATION_SCOPE;

    // Encode state (JWT) and query parts — raw JWT in URL can break OAuth when it contains +, /, or =.
    const oAuthUrl =
        jwt && clientId
            ? `https://signin.tradestation.com/authorize?response_type=code&client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}&audience=${encodeURIComponent(audience || '')}&state=${encodeURIComponent(jwt)}&scope=${encodeURIComponent(scope || '')}`
            : '';
  
    const getAccessToken = async (code) => {
        const url = `${getBackendUrl()}/tradestation/oauth`;
        const body = { code, redirectUri };
        return request(url, {}, 'POST', body);
    };
    
    const refreshToken = async (refresh_token) => {
        const url = `${getBackendUrl()}/tradestation/refresh_token`;
        return request(url, {},'PUT');
    };

    return { loading, oAuthUrl, getAccessToken, refreshToken };
};

export const useCreateConnectTradeConnection = () => {
    const { data, error, loading, request } = useAPI();

    const createConnection = (jwt, body) => {
        const url = `${getBackendUrl()}/connecttrade/connections`;
        return request(url, { Authorization: `Bearer ${jwt}` }, 'POST', body);
    };

    return { data, error, loading, createConnection };
};

export const useDisconnectBrokerConnection = () => {
    const { data, error, loading, request } = useAPI();

    const disconnectConnection = (jwt, connectionId) => {
        const url = `${getBackendUrl()}/broker/connections/${connectionId}`;
        return request(url, { Authorization: `Bearer ${jwt}` }, 'DELETE');
    };

    return { data, error, loading, disconnectConnection };
};

export const useGetUserSettings = () => {
    const { data, error, loading, request } = useAPI();

    const getUserSettings = (jwt) => {
        const url = `${getBackendUrl()}/auth/settings`;
        return request(url, { 'Authorization' : `Bearer ${jwt}`}, 'GET');
    };

    return { data, error, loading, getUserSettings };
};

export const useUpdateUserSettings = () => {
    const { data, error, loading, request } = useAPI();

    const updateUserSettings = (jwt, settings) => {
        const url = `${getBackendUrl()}/auth/settings`;
        return request(url, { 'Authorization': `Bearer ${jwt}` }, 'PUT', settings);
    };

    return { data, error, loading, updateUserSettings };
};

export const useUpdateAppSettings = () => {
    const { data, error, loading, request } = useAPI();

    // Stable reference — request itself is stable (empty useCallback deps in useAPI),
    // so updateAppSettings never changes identity across renders.
    // `patch` is only the fields this call changes, or a function of the latest copy.
    const updateAppSettings = useCallback((jwt, patch) => {
        if (!jwt) return Promise.resolve(null);
        const before = JSON.stringify(getAppSettings());
        const next = commitAppSettingsPatch(patch);
        if (!isAppSettingsSeeded()) return Promise.resolve(next);
        if (JSON.stringify(next) === before) return Promise.resolve(next);
        const url = `${getBackendUrl()}/auth/settings`;
        return enqueueAppSettingsSave((body) => {
            const sanitized = stripServerLayoutSettings(body);
            return request(url, { 'Authorization': `Bearer ${jwt}` }, 'PUT', { appSettings: sanitized });
        });
    }, [request]);

    return { data, error, loading, updateAppSettings };
};

export const useUpdateAccountDefaults = () => {
    const { data, error, loading, request } = useAPI();

    const updateAccountDefaults = (jwt, accountDefaults) => {
        const url = `${getBackendUrl()}/auth/settings`;
        return request(url, { 'Authorization': `Bearer ${jwt}` }, 'PUT', { accountDefaults });
    };

    return { data, error, loading, updateAccountDefaults };
};

// Chart Drawings Backend Methods (per-ticker drawing persistence)
// Load: use useChartDrawingsQuery (React Query) for caching/deduplication
// Save: saveChartDrawings (imperative) — `drawings` is a plain array; request body is { drawings: [...] }
export const useChartDrawings = () => {
    const { data, error, loading, request } = useAPI();

    const saveChartDrawings = useCallback((jwt, ticker, drawings) => {
        const url = `${getBackendUrl()}/chart_drawings/${encodeURIComponent(ticker)}`;
        const payload = Array.isArray(drawings) ? drawings : [];
        return request(url, { 'Authorization': `Bearer ${jwt}` }, 'PUT', { drawings: payload });
    }, [request]);

    return { data, error, loading, saveChartDrawings };
};

// Ticker Backend Methods
export const useGetTickerOptions = () => {
    const { data, error, loading, request } = useAPI();

    const getTickerOptions = () => {
        const url = `${getBackendUrl()}/ticker_options`;
        return request(url, {}, 'GET');
    };

    return { data, error, loading, getTickerOptions };
};

// Trade Alerts Backend Methods
export const useGetTradeAlerts = () => {
    const { data, error, loading, request } = useAPI();

    const getTradeAlerts = (jwt) => {
        const url = `${getBackendUrl()}/trade_alerts`;
        return request(url, { 'Authorization' : `Bearer ${jwt}`}, 'GET');
    };

    return { data, error, loading, getTradeAlerts };
};

export const useCreateTradeAlert = () => {
    const { data, error, loading, request } = useAPI();

    const createTradeAlert = (jwt, alertData) => {
        const url = `${getBackendUrl()}/trade_alerts`;
        return request(url, {}, 'POST', alertData);
    };

    return { data, error, loading, createTradeAlert };
};

export const useUpdateTradeAlert = () => {
    const { data, error, loading, request } = useAPI();

    const updateTradeAlert = (jwt, alertId, alertData) => {
        const url = `${getBackendUrl()}/trade_alerts/${alertId}`;
        return request(url, {}, 'PUT', alertData);
    };

    return { data, error, loading, updateTradeAlert };
};

export const useDeleteTradeAlert = () => {
    const { data, error, loading, request } = useAPI();

    const deleteTradeAlert = (jwt, alertId) => {
        const url = `${getBackendUrl()}/trade_alerts/${alertId}`;
        return request(url, {}, 'DELETE');
    };

    return { data, error, loading, deleteTradeAlert };
};

// Standard Deviation Levels Backend Methods
export const useGetStdDevLevels = () => {
    const { data, error, loading, request } = useAPI();

    const getStdDevLevels = (jwt, ticker, timeframe = '1hour') => {
        const url = `${getBackendUrl()}/std_dev_levels/${ticker}?timeframe=${timeframe}`;
        return request(url, { 'Authorization' : `Bearer ${jwt}`}, 'GET');
    };

    return { data, error, loading, getStdDevLevels };
};

export const useUpdateAllStdDevTimeframes = () => {
    const { data, error, loading, request } = useAPI();

    const updateAllStdDevTimeframes = (jwt, ticker) => {
        const url = `${getBackendUrl()}/std_dev_levels/${ticker}/update_all`;
        return request(url, {}, 'POST');
    };

    return { data, error, loading, updateAllStdDevTimeframes };
};

// Technical Indicators Backend Methods
export const useGetTechnicalIndicators = () => {
    const { data, error, loading, request } = useAPI();

    const getTechnicalIndicators = (jwt, ticker, timeframe = '1hour') => {
        const url = `${getBackendUrl()}/indicators/${ticker}?timeframe=${timeframe}`;
        return request(url, { 'Authorization' : `Bearer ${jwt}`}, 'GET');
    };

    return { data, error, loading, getTechnicalIndicators };
};

export const useGetSpecificIndicator = () => {
    const { data, error, loading, request } = useAPI();

    const getSpecificIndicator = (jwt, ticker, indicator, timeframe = '1hour', period = null) => {
        let url = `${getBackendUrl()}/indicators/${ticker}/${indicator}?timeframe=${timeframe}`;
        if (period) {
            url += `&time_period=${period}`;
        }
        return request(url, {}, 'GET');
    };

    return { data, error, loading, getSpecificIndicator };
};

export const useDeleteAnalyticsEvents = () => {
    const { data, error, loading, request } = useAPI();

    const deleteAnalyticsEvents = (jwt, userId) => {
        const url = `${getBackendUrl()}/analytics/events/user/${userId}`;
        return request(url, {}, 'DELETE');
    };

    return { data, error, loading, deleteAnalyticsEvents };
};

// Bug Reports Backend Methods
export const useSubmitBugReport = () => {
    const { data, error, loading, request } = useAPI();

    const submitBugReport = (bugReportData) => {
        const url = `${getBackendUrl()}/bug-reports`;
        return request(url, {}, 'POST', bugReportData);
    };

    return { data, error, loading, submitBugReport };
};

// Admin: Reset User Lockouts
export const useResetUserLockouts = () => {
    const { data, error, loading, request } = useAPI();

    const resetUserLockouts = (userId) => {
        const url = `${getBackendUrl()}/enforcement/admin/reset-user-lockouts`;
        return request(url, {}, 'POST', { userId });
    };

    return { data, error, loading, resetUserLockouts };
};

// Push Notification Subscription Hooks
export const useRegisterPushSubscription = () => {
    const { data, error, loading, request } = useAPI();

    const registerPushSubscription = (subscriptionData) => {
        const url = `${getBackendUrl()}/push/subscribe`;
        return request(url, {}, 'POST', subscriptionData);
    };

    return { data, error, loading, registerPushSubscription };
};

export const useUnregisterPushSubscription = () => {
    const { data, error, loading, request } = useAPI();

    const unregisterPushSubscription = (subscriptionData) => {
        const url = `${getBackendUrl()}/push/unsubscribe`;
        return request(url, {}, 'DELETE', subscriptionData);
    };

    return { data, error, loading, unregisterPushSubscription };
};